import React from 'react';

import { formatDate } from '@betterment/core';
import { Box, Dialog, H2, ListRow, Tag, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { ActiveRewardsQuery } from './graphql/query.activeRewards.generated';
import RewardTiersTable from './RewardTiersTable';

export type Props = {
  activeReward: ActiveRewardsQuery['activeRewards'][number];
  openModal: boolean;
  onOpenChange: () => void;
};

const RewardDetailsDialog: React.FC<Props> = ({ activeReward, openModal, onOpenChange }) => {
  if (!activeReward) return null;

  const {
    rewardProgressStatuses,
    tiers,
    termsUrl,
    promotionWindow,
    isCompleted,
    shortDescription,
    fulfillmentConditionDescription,
    fulfillmentRewardDescription,
  } = activeReward;

  return (
    <Dialog open={openModal} onOpenChange={onOpenChange} overlay="default">
      <Dialog.Content
        css={{
          p: 'space.400',
          overflow: 'scroll',
        }}
        size="large"
      >
        <Dialog.Title asChild={true}>
          <H2 variant="title300" css={{ pb: 'space.200' }}>
            {isCompleted && (
              <Tag accent="grey" css={{ display: 'inline-flex', mb: 'space.50' }}>
                Offer ended {formatDate(promotionWindow.end)}
              </Tag>
            )}
            <Box as="span" css={{ display: 'block' }}>
              {shortDescription}
            </Box>
          </H2>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Box>
            <Text style={{ whiteSpace: 'pre-line' }}>{activeReward.detailedDescription}</Text>

            <Text variant="caption" color="text.weak" css={{ display: 'block', pt: 'space.200' }}>
              {activeReward.disclosureDescription}{' '}
              <SambaAutoLink to={termsUrl} target="_blank">
                See terms
              </SambaAutoLink>
            </Text>

            {tiers.length > 0 && (
              <RewardTiersTable
                rewardTiers={tiers}
                rewardConditionTitle={fulfillmentConditionDescription}
                rewardTitle={fulfillmentRewardDescription}
              />
            )}

            {rewardProgressStatuses.map((currentProgressStatus, index) => {
              const isLastItem = rewardProgressStatuses.length - 1 === index;
              return (
                <ListRow
                  style={{ padding: `2rem 0 ${isLastItem ? '0' : '2rem'}` }}
                  css={{
                    borderBottom: isLastItem ? '' : 'token(borderWidths.thin) solid',
                    borderColor: isLastItem ? '' : 'border',
                    marginTop: isLastItem ? '' : 'space.100',
                  }}
                  key={currentProgressStatus.id}
                >
                  <ListRow.LeadingContent>
                    <Text as="p" id={currentProgressStatus.id}>
                      {currentProgressStatus.briefDescription}
                    </Text>

                    {isCompleted && currentProgressStatus.detailedDescription && (
                      <Text as="p" variant="text50" color="text.weak">
                        {currentProgressStatus.detailedDescription}
                      </Text>
                    )}
                  </ListRow.LeadingContent>
                  <ListRow.TrailingContent>
                    <Text as="p" aria-labelledby={currentProgressStatus.id}>
                      {currentProgressStatus.value}
                    </Text>
                  </ListRow.TrailingContent>
                </ListRow>
              );
            })}
          </Box>
        </Dialog.Description>
      </Dialog.Content>
    </Dialog>
  );
};
export default RewardDetailsDialog;
